import React, { useMemo } from 'react';
import { usePluginRoutes } from './usePluginRoutes';
import { RegisteredRoute } from './types';

export interface AppRoute {
  path: string;
  element: React.ReactElement;
  order?: number;// Optional action for permission checks
  action?: string; // Optional action for permission checks
  [key: string]: any; // Allow additional properties
}

export interface PluginAppRoute extends AppRoute {
  isPluginRoute: true;
  pluginName: string;
  title?: string;
  action?: string;
  originalRoute: RegisteredRoute;
}

export interface AppRoutesWithPluginsResult {
  allRoutes: (AppRoute | PluginAppRoute)[];
  appRoutes: AppRoute[];
  pluginRoutes: PluginAppRoute[];
  routesByPlugin: Record<string, PluginAppRoute[]>;
}

export interface UseAppRoutesWithPluginsOptions {
  /**
   * Default order for plugin routes (higher numbers appear later)
   * @default 1000
   */
  defaultPluginOrder?: number;
  
  /**
   * Namespace to filter plugin routes
   */
  namespace?: string;
  
  /**
   * Custom function to convert plugin routes to app route format
   */
  convertPluginRoute?: (pluginRoute: RegisteredRoute) => Partial<AppRoute>;
  
  /**
   * Custom sorting function for merged routes
   */
  sortRoutes?: (a: AppRoute | PluginAppRoute, b: AppRoute | PluginAppRoute) => number;
  
  /**
   * Filter function for plugin routes
   */
  filterPluginRoutes?: (pluginRoute: RegisteredRoute) => boolean;
}

/**
 * Hook to merge app routes with plugin routes
 * 
 * @param appRoutes - Your existing app routes array
 * @param options - Configuration options
 * @returns Object containing merged routes and utilities
 * 
 * @example
 * ```typescript
 * const appRoutes = [
 *   { path: '/dashboard', element: <Dashboard />, order: 10 }
 * ];
 * 
 * const { allRoutes, pluginRoutes } = useAppRoutesWithPlugins(appRoutes);
 * 
 * // Use in your Routes component
 * {allRoutes.map(route => (
 *   <Route key={route.path} {...route} />
 * ))}
 * ```
 */
export const useAppRoutesWithPlugins = (
  appRoutes: AppRoute[],
  options: UseAppRoutesWithPluginsOptions = {}
): AppRoutesWithPluginsResult => {
  const {
    defaultPluginOrder = 1000,
    namespace,
    convertPluginRoute,
    sortRoutes,
    filterPluginRoutes
  } = options;

  const { getRoutes } = usePluginRoutes();
  
  const result = useMemo(() => {
    // Get plugin routes, optionally filtered by namespace
    let pluginRoutes = getRoutes(namespace);
    
    // Apply custom filter if provided
    if (filterPluginRoutes) {
      pluginRoutes = pluginRoutes.filter(filterPluginRoutes);
    }
    
    // Convert plugin routes to app route format
    const convertedPluginRoutes: PluginAppRoute[] = pluginRoutes.map(pluginRoute => {
      const RouteComponent = pluginRoute.component;
      
      const baseConversion = {
        path: pluginRoute.path,
        element: React.createElement(RouteComponent),
        action: pluginRoute.action,
        order: pluginRoute.order ?? defaultPluginOrder,
        title: pluginRoute.title
      };

      // Apply custom conversion if provided
      const customProps = convertPluginRoute ? convertPluginRoute(pluginRoute) : {};

      return {
        ...baseConversion,
        ...customProps,
        // These properties are always set for plugin routes
        isPluginRoute: true as const,
        pluginName: pluginRoute.pluginName,
        originalRoute: pluginRoute
      };
    });

    // Merge and sort routes
    const mergedRoutes = [...appRoutes, ...convertedPluginRoutes];
    
    // Apply custom sorting or default sorting
    if (sortRoutes) {
      mergedRoutes.sort(sortRoutes);
    } else {
      mergedRoutes.sort((a, b) => {
        const aOrder = a.order ?? 0;
        const bOrder = b.order ?? 0;
        return aOrder - bOrder;
      });
    }

    // Group plugin routes by plugin name
    const routesByPlugin = convertedPluginRoutes.reduce((acc, route) => {
      if (!acc[route.pluginName]) {
        acc[route.pluginName] = [];
      }
      acc[route.pluginName].push(route);
      return acc;
    }, {} as Record<string, PluginAppRoute[]>);

    return {
      allRoutes: mergedRoutes,
      appRoutes,
      pluginRoutes: convertedPluginRoutes,
      routesByPlugin
    };
  }, [appRoutes, getRoutes, namespace, defaultPluginOrder, convertPluginRoute, sortRoutes, filterPluginRoutes]);

  return result;
};

/**
 * Hook specifically for React Router integration
 * Provides routes in the format expected by React Router v6
 */
export const useReactRouterAppRoutes = (
  appRoutes: AppRoute[],
  options: UseAppRoutesWithPluginsOptions = {}
) => {
  const { allRoutes, ...rest } = useAppRoutesWithPlugins(appRoutes, options);
  
  // Convert to React Router v6 format
  const reactRouterRoutes = useMemo(() => {
    return allRoutes.map(route => ({
      path: route.path,
      element: route.element,
      // Include any additional props that React Router might need
      ...(route.children && { children: route.children }),
      ...(route.index && { index: route.index }),
      ...(route.caseSensitive && { caseSensitive: route.caseSensitive })
    }));
  }, [allRoutes]);

  return {
    routes: reactRouterRoutes,
    allRoutes,
    ...rest
  };
};

/**
 * Utility hook to get routes for a specific plugin
 */
export const usePluginAppRoutes = (pluginName: string, appRoutes: AppRoute[] = []) => {
  const { routesByPlugin } = useAppRoutesWithPlugins(appRoutes);
  return routesByPlugin[pluginName] || [];
};

/**
 * Hook to check if a plugin has registered any routes
 */
export const useHasPluginRoutes = (pluginName?: string) => {
  const { pluginRoutes, routesByPlugin } = useAppRoutesWithPlugins([]);
  
  if (pluginName) {
    return (routesByPlugin[pluginName]?.length ?? 0) > 0;
  }
  
  return pluginRoutes.length > 0;
};